<template>
  <v-container>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="700px"
      :scrollable="$vuetify.breakpoint.xsOnly"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-form v-model="valid" ref="itemForm" @submit.prevent="item_submit">
        <v-card tile height="100%">
          <v-card-title class="pa-0">
            <v-app-bar color="primary" dark>
              <v-app-bar-title>
                {{ newItem ? "Nuevo Empleado" : "Editar Empleado" }}
              </v-app-bar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="close()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-app-bar>
          </v-card-title>

          <v-card-text class="pt-5">
            <!-- Avatar del usuario -->
            <v-row justify="center" v-if="!newItem">
              <v-col cols="12" sm="3" class="text-center">
                <v-avatar size="100" color="primary" class="mb-3">
                  <img
                    v-if="item.user_photo"
                    :src="getPhotoUrl(item.user_photo)"
                    alt="avatar"
                    @error="item.user_photo = null"
                  />
                  <span v-else class="white--text headline">
                    {{ getInitials(item.user_name) }}
                  </span>
                </v-avatar>
              </v-col>
            </v-row>

            <v-row>
              <!-- Nombre completo -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.user_name"
                  id="user_name"
                  :rules="f_required"
                  label="Nombre completo"
                  hide-details="auto"
                  required
                  dense
                  outlined
                  @keyup="returnCap"
                  prepend-inner-icon="mdi-account"
                ></v-text-field>
              </v-col>

              <!-- Email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.user_email"
                  id="user_email"
                  :rules="f_email"
                  label="Correo Electrónico"
                  hide-details="auto"
                  required
                  dense
                  outlined
                  :readonly="!newItem"
                  prepend-inner-icon="mdi-email-outline"
                  @keyup="formatEmail"
                >
                </v-text-field>
              </v-col>

              <!-- Contraseña -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="item.user_password"
                  label="Contraseña"
                  ref="user_password"
                  :append-icon="password_value ? 'mdi-eye-outline' : 'mdi-eye'"
                  @click:append="() => (password_value = !password_value)"
                  :type="password_value ? 'password' : 'text'"
                  hide-details="auto"
                  dense
                  outlined
                  prepend-inner-icon="mdi-lock-outline"
                  :hint="
                    newItem
                      ? 'Mínimo 6 caracteres'
                      : 'Dejar en blanco para mantener la contraseña actual'
                  "
                  persistent-hint
                  :rules="passwordRules"
                />
              </v-col>

              <!-- Nivel de acceso -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="item.user_access"
                  ref="user_access"
                  :items="filteredAccessLevels"
                  item-text="text"
                  item-value="value"
                  label="Rol en la Tienda"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="f_required"
                  required
                  prepend-inner-icon="mdi-shield-account"
                >
                </v-select>
              </v-col>

              <!-- Resto del formulario igual... -->
              <!-- Código de usuario (readonly) -->
              <v-col cols="12" md="6" v-if="!newItem">
                <v-text-field
                  v-model="item.user_code"
                  label="Código de Empleado"
                  outlined
                  dense
                  readonly
                  hide-details="auto"
                  prepend-inner-icon="mdi-identifier"
                />
              </v-col>

              <!-- Estado del token -->
              <v-col cols="12" md="6" v-if="!newItem">
                <v-select
                  v-model="item.token_active"
                  :items="[
                    { text: 'Acceso Activo', value: '1' },
                    { text: 'Acceso Suspendido', value: '0' },
                  ]"
                  item-text="text"
                  item-value="value"
                  label="Estado de Acceso"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-key"
                />
              </v-col>

              <!-- Información de último acceso -->
              <v-col cols="12" v-if="!newItem && item.issue_date">
                <v-alert type="info" text outlined dense>
                  <v-icon small class="mr-2">mdi-information-outline</v-icon>
                  Último acceso: {{ formatDate(item.issue_date) }}
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-btn
              v-if="!newItem"
              color="error"
              text
              @click="delete_itm(item)"
              :disabled="item.user_access === 'ADMIN'"
            >
              <v-icon left>mdi-delete</v-icon>
              <span v-if="item.user_access === 'ADMIN'">No se puede eliminar ADMIN</span>
              <span v-else>Eliminar</span>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="close()"> Cancelar </v-btn>
            <v-btn
              color="primary"
              @click="item_submit"
              :loading="loading_status"
              :disabled="!valid"
            >
              <v-icon left>mdi-content-save</v-icon>
              {{ newItem ? "Crear Empleado" : "Actualizar" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Diálogo de confirmación para eliminar -->
    <v-dialog persistent max-width="400" v-model="dialog_confirm">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirmar eliminación
        </v-card-title>
        <v-card-text>
          ¿Está seguro de que desea eliminar a
          <strong>{{ item.user_name }}</strong
          >? <br /><br />
          Esta acción no se puede deshacer y el empleado perderá acceso al sistema.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog_confirm = false">Cancelar</v-btn>
          <v-btn color="error" @click="delete_item" :loading="loading_status">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";
import createItem from "@/utils/createUser.js";

export default {
  name: "UserForm",

  props: {
    table: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
      default: () => createItem(),
    },
    dialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    newItem: {
      type: Boolean,
      required: true,
      default: false,
    },
  },

  data() {
    return {
      password_value: true,
      loading_status: false,
      dialog_confirm: false,
      valid: false,
      f_required: [(v) => !!v || "Este campo es requerido"],
      f_email: [
        (v) => !!v || "El correo electrónico es requerido",
        (v) => /.+@.+\..+/.test(v) || "Ingrese un correo electrónico válido",
      ],
      passwordRules: [
        (v) => (this.newItem ? !!v || "La contraseña es requerida" : true),
        (v) =>
          this.newItem
            ? (v && v.length >= 6) || "La contraseña debe tener al menos 6 caracteres"
            : true,
      ],

      // Niveles de acceso específicos para retail
      access_levels: [
        {
          value: "POS",
          text: "Cajero - Punto de Venta",
          icon: "mdi-cash-register",
          description: "Acceso solo al sistema de ventas",
        },
        {
          value: "VENDEDOR",
          text: "Vendedor - Atención Cliente",
          icon: "mdi-account-tie",
          description: "Ventas y gestión de clientes",
        },
        {
          value: "AUDITOR",
          text: "Auditor - Supervisión",
          icon: "mdi-clipboard-check",
          description: "Control y supervisión de operaciones",
        },
        {
          value: "SUPERVISOR",
          text: "Supervisor - Gestión",
          icon: "mdi-account-supervisor",
          description: "Gestión de turnos y personal",
        },
        {
          value: "StoreAdmin",
          text: "Admin Tienda - Configuración",
          icon: "mdi-store-cog",
          description: "Configuración de tienda y gestión de personal",
        },
        {
          value: "Analista",
          text: "Analista - Compras y Datos",
          icon: "mdi-chart-bell-curve-cumulative",
          description: "Análisis de datos y gestión de compras/proveedores",
        },
        {
          value: "ADMIN",
          text: "Administrador - Completo",
          icon: "mdi-shield-crown",
          description: "Acceso completo al sistema",
        },
      ],

      // Configuración de colores por rol
      roleColors: {
        ADMIN: "red darken-1",
        POS: "blue",
        VENDEDOR: "green",
        AUDITOR: "orange",
        SUPERVISOR: "indigo",
        StoreAdmin: "deep-orange",
        Analista: "teal",
      },

      storeLst: [],
    };
  },

  computed: {
    // Store actual del usuario
    currentUserStore() {
      const currentUser = this.$store?.state?.profile || window.profile;
      return currentUser?.store_id;
    },

    // Usuario actual
    currentUser() {
      return this.$store?.state?.profile || window.profile;
    },

    // Niveles de acceso filtrados según permisos
    filteredAccessLevels() {
      const userAccess = this.currentUser?.user_access;

      if (userAccess === "ADMIN") {
        return this.access_levels;
      }

      // StoreAdmin puede asignar roles operativos y Analista, pero no ADMIN ni StoreAdmin
      if (userAccess === "StoreAdmin") {
        return this.access_levels.filter(
          (level) => level.value !== "ADMIN" && level.value !== "StoreAdmin"
        );
      }

      // Otros roles no pueden crear ADMIN ni StoreAdmin
      return this.access_levels.filter(
        (level) => level.value !== "ADMIN" && level.value !== "StoreAdmin"
      );
    },
  },

  mounted() {
    this.get_stores();

    // Si es un item nuevo, resetear campos
    if (this.newItem) {
      this.item.user_password = "";
      this.item.token_active = "1";
      this.item.store_id = window.profile.store_id;
    }
  },

  methods: {
    // Obtener las iniciales del nombre para el avatar
    getInitials(name) {
      if (!name) return "?";
      const names = name.split(" ");
      if (names.length === 1) return names[0].charAt(0).toUpperCase();
      return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
    },

    // Obtener URL de la foto
    getPhotoUrl(photoData) {
      if (!photoData) return null;

      if (photoData.startsWith("data:image/")) {
        return photoData;
      } else if (photoData.length > 100) {
        return `data:image/jpeg;base64,${photoData}`;
      } else {
        return photoData;
      }
    },

    // Obtener color del rol
    getRoleColor(role) {
      return this.roleColors[role] || "grey";
    },

    // Formatar texto en mayúsculas
    returnCap(event) {
      this.item[event.target.id] = event.target.value.toUpperCase();
    },

    // Formatar email en minúsculas
    formatEmail(event) {
      this.item[event.target.id] = event.target.value.toLowerCase();
    },

    // Validación y envío del formulario
    item_submit() {
      const currentUser = this.currentUser;

      // Solo ADMIN puede crear/editar usuarios
      if (currentUser.user_access !== "ADMIN") {
        this.showError("Solo los administradores pueden gestionar personal");
        return;
      }

      // Validar formulario
      if (!this.$refs.itemForm.validate()) {
        return;
      }

      // Forzar store_id a la tienda actual
      this.item.store_id = this.currentUserStore;

      this.validateAndSubmit();
    },

    // Validar y enviar
    validateAndSubmit() {
      if (!this.item.user_name || !this.item.user_email || !this.item.user_access) {
        this.showError("Complete todos los campos requeridos");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.item.user_email)) {
        this.showError("Ingrese un email válido");
        return;
      }

      if (
        this.newItem &&
        (!this.item.user_password || this.item.user_password.length < 6)
      ) {
        this.showError("La contraseña debe tener al menos 6 caracteres");
        return;
      }

      this.submitUser();
    },

    // Enviar usuario
    submitUser() {
      console.log('🔄 Iniciando envío de usuario...');
      console.log('📝 Datos del usuario:', {
        newItem: this.newItem,
        user_name: this.item.user_name,
        user_email: this.item.user_email,
        user_access: this.item.user_access,
        store_id: this.item.store_id,
        currentUserStore: this.currentUserStore
      });
      
      this.loading_status = true;

      // CORREGIR: Enviar datos como espera el backend
      const storeId = this.currentUserStore || this.item.store_id || window.profile?.store_id;
      const userEmail = this.item.user_email;
      
      console.log('📋 Verificando datos requeridos:');
      console.log('- store_id:', storeId);
      console.log('- user_email:', userEmail);
      
      if (!storeId) {
        console.error('❌ ERROR: store_id no disponible');
        this.loading_status = false;
        this.showError('Error: ID de tienda no disponible');
        return;
      }
      
      if (!userEmail) {
        console.error('❌ ERROR: user_email no disponible');
        this.loading_status = false;
        this.showError('Error: Email de usuario requerido');
        return;
      }

      // ESTRUCTURA CORRECTA: Como espera el backend PHP
      const qry = {
        user: {
          store_id: storeId,
          user_email: userEmail,
          user_name: this.item.user_name,
          user_access: this.item.user_access,
          token_active: this.item.token_active || "1",
          user_code: this.item.user_code || this.generateUserCode(),
        }
      };

      // Agregar contraseña si existe
      if (this.item.user_password) {
        console.log('🔐 Procesando contraseña...');
        try {
          const md5 = require("md5");
          qry.user.user_password = md5(this.item.user_password);
          console.log('✅ Contraseña encriptada correctamente');
        } catch (error) {
          console.error('❌ Error al encriptar contraseña:', error);
          this.loading_status = false;
          this.showError('Error al procesar la contraseña');
          return;
        }
      }

      // Agregar foto si existe
      if (this.item.user_photo) {
        console.log('🖼️ Agregando foto del usuario...');
        qry.user.user_photo = this.item.user_photo;
      }

      console.log('📤 Query FINAL (backend compatible):', {
        user: {
          ...qry.user,
          user_password: qry.user.user_password ? '[ENCRYPTED]' : 'NO_PASSWORD'
        }
      });

      console.log('🌐 Llamando endpoint save_user...');
      
      // Timeout para detectar si se queda colgado
      const timeoutId = setTimeout(() => {
        console.log('⏰ TIMEOUT: El endpoint save_user está tardando más de 15 segundos');
        this.loading_status = false;
        this.showError('La operación está tardando mucho. Verifique su conexión.');
      }, 15000);

      // Usar endpoint específico
      webserver("save_user", qry, (response) => {
        clearTimeout(timeoutId);
        this.loading_status = false;
        
        console.log('📥 Respuesta del servidor:', response);

        if (response && response.error) {
          console.log('❌ Error del servidor:', response.message);
          this.showError("Error al guardar: " + response.message);
        } else {
          console.log('✅ Usuario guardado exitosamente');
          this.showSuccess(
            this.newItem
              ? "Empleado agregado correctamente"
              : "Empleado actualizado correctamente"
          );
          this.$emit("refresh");
        }
      });
    },

    // Generar código de usuario
    generateUserCode() {
      return "USR" + Date.now().toString().slice(-6);
    },

    // Eliminar usuario
    delete_itm() {
      this.dialog_confirm = true;
    },

    // Ejecutar eliminación
    delete_item() {
      console.log('🗑️ Iniciando eliminación de usuario...');
      console.log('📋 Usuario a eliminar:', {
        user_name: this.item.user_name,
        user_email: this.item.user_email,
        user_access: this.item.user_access,
        store_id: this.item.store_id
      });
      
      this.loading_status = true;

      // Verificar que no es admin (validación adicional)
      if (this.item.user_access === 'ADMIN') {
        console.error('❌ ERROR: No se puede eliminar un administrador');
        this.loading_status = false;
        this.dialog_confirm = false;
        this.showError('No se puede eliminar un administrador');
        return;
      }

      // Estructura correcta para el endpoint delete_user
      const qry = {
        store: this.currentUserStore || window.profile?.store_id,
        user_email: this.item.user_email
      };
      
      console.log('📤 Query para delete_user:', qry);
      console.log('🌐 Llamando endpoint delete_user...');

      // Usar el endpoint específico delete_user
      webserver("delete_user", qry, (response) => {
        this.loading_status = false;
        this.dialog_confirm = false;
        
        console.log('📥 Respuesta del servidor:', response);

        if (response && response.error) {
          console.log('❌ Error del servidor:', response.message);
          this.showError(
            "Error al eliminar: " + (response.message || "Error desconocido")
          );
        } else {
          console.log('✅ Usuario eliminado exitosamente');
          this.showSuccess("Empleado eliminado correctamente");
          this.$emit("refresh");
        }
      });
    },

    // Cerrar formulario
    close() {
      this.$emit("close");
    },

    // Obtener tiendas
    get_stores() {
      const currentUser = this.currentUser;

      this.storeLst = [
        {
          store_id: currentUser.store_id,
          store_name: currentUser.store_name || "Mi Tienda",
          name: currentUser.store_name || "Mi Tienda",
        },
      ];

      if (this.newItem) {
        this.item.store_id = currentUser.store_id;
      }
    },

    // Formatar fecha
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },

    // Métodos helper para mensajes
    showError(message) {
      console.log('❌ Mostrando error:', message);
      
      // Intentar usar el store de Vuex si está disponible
      if (this.$store && this.$store.dispatch) {
        try {
          this.$store.dispatch('showMessage', {
            color: 'error',
            message: message,
          });
          return;
        } catch (e) {
          console.warn('Store no disponible, usando alert:', e.message);
        }
      }
      
      // Fallback: usar alert nativo
      alert('Error: ' + message);
    },

    showSuccess(message) {
      console.log('✅ Mostrando éxito:', message);
      
      // Intentar usar el store de Vuex si está disponible
      if (this.$store && this.$store.dispatch) {
        try {
          this.$store.dispatch('showMessage', {
            color: 'success',
            message: message,
          });
          return;
        } catch (e) {
          console.warn('Store no disponible, usando alert:', e.message);
        }
      }
      
      // Fallback: usar alert nativo
      alert('Éxito: ' + message);
    },
  },
};
</script>

<style scoped>
.v-avatar {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}
</style>
